

import { TextureLoader, Texture, WebGLRenderer, WebGLRenderTarget, OrthographicCamera, CompressedTexture, CubeTexture, MeshBasicMaterial, Scene, PlaneGeometry, Mesh, TypedArray, EquirectangularReflectionMapping } from "three";
import { IValueConverter, ValueConverterOptions, ValueConversionService } from "../../properties/common/converter";
import { isCompressedTexture, isCubeTexture, isDataTexture, isTexture, isWebGLRenderTarget, ktx2Loader, loadTexture } from "./helper";
import { _runWhenIdle, Queue, runWhenGlobalIdle, TaskQueue, Throttler, timeout } from "../../../base/common/async";
import type { ThreeJs } from "./webgl.js";
import { Disposable } from "../../../base/common/lifecycle";
import { shallowCloneObject } from "../../../base/common/helpers";
import { ILogService } from "../../log/common/log";
import { BugIndicatingError } from "../../../base/common/errors";
import { HierarchyNode } from "../../hierarchy/common/hierarchyService";
import { NodeFlags } from "../../hierarchy/common/hierarchy";



// #region Texture Converter

interface TextureBlobCacheEntry {
    blobUrl: string;
    version: number;
    width: number;
    height: number;
}

export class TextureConverter extends Disposable implements IValueConverter {

    private readonly queue = new TaskQueue();
    /** Cache for URL string → Texture */
    private readonly stringToTextureCache = new Map<string, Texture>();
    /** Cache for Texture → blob URL (uses WeakMap so entries are GC'd when textures are disposed) */
    private readonly textureToBlobCache = new WeakMap<Texture, TextureBlobCacheEntry>();

    constructor(
        private readonly three: ThreeJs,
        @ILogService private readonly logger: ILogService,
    ) {
        super();
    }

    dispose(): void {
        super.dispose();
        this.stringToTextureCache.clear();
        // WeakMap doesn't need clearing - entries are automatically removed when textures are GC'd
    }

    canConvert(value: unknown, context: ValueConverterOptions): boolean {
        if (typeof value === "string") {
            const str = value as string;
            if (str.startsWith("data:image/") || str.match(/\.(png|jpg|jpeg|gif|bmp|webp|svg|exr|hdr|hdri|ktx2)(\?|$)/i)) {
                if (context.targetType === "texture") return true;
                if (context.targetType === "blob:url") return true;
            }
        }
        else if (isTexture(value)) {
            if (context.targetType === "texture") return true;
            if (context.targetType === "blob:url") return true;
        }
        return false;
    }

    async convert<T extends unknown>(value: unknown, _options: ValueConverterOptions): Promise<T | null> {

        if (!value) {
            throw new BugIndicatingError("TextureConverter: Cannot convert null or undefined value.");
        }

        const options = { ..._options };

        return this.queue.schedule(async () => {

            if (isTexture(value) && options.targetType === "texture") {
                return value as unknown as T;
            }

            await new Promise(res => runWhenGlobalIdle(res));

            if(_options.token?.isCancellationRequested) {
                this.logger.warn("TextureConverter: Conversion cancelled before start.");
                return null;
            }

            // The target is a texture
            if (options.targetType === "texture") {
                if (typeof value === "string") {
                    const str = value as string;
                    if (str.startsWith("data:image/") || str.match(/\.(png|jpg|jpeg|gif|bmp|webp|svg|exr|hdr|hdri|ktx2)(\?|$)/i)) {
                        if (this.stringToTextureCache.has(value)) {
                            return this.stringToTextureCache.get(value) as unknown as T;
                        }
                        try {
                            const texture = await loadTexture(str);
                            if(texture) {
                                this.stringToTextureCache.set(value, texture);
                                switch(options.usageHint) {
                                    case "skybox":
                                        texture.mapping = EquirectangularReflectionMapping;
                                        break;
                                }
                            }
                            return texture as unknown as T;
                        }
                        catch (e) {
                            console.error("Failed to load texture from string:", e);
                        }
                    }
                }
                else {
                    this.logger.warn("TextureConverter: Cannot convert value to texture (value type is " + typeof value + ")");
                }
            }
            // The target type
            else if (options.targetType === "blob:url") {

                if (isTexture(value)) {
                    const texture = value as Texture;
                    const requestedWidth = options.width || 0;
                    const requestedHeight = options.height || 0;

                    // Check WeakMap cache - entries are automatically cleaned up when textures are GC'd
                    const cached = this.textureToBlobCache.get(texture);
                    if (cached &&
                        cached.version === texture.version &&
                        cached.width === requestedWidth &&
                        cached.height === requestedHeight) {
                        return cached.blobUrl as unknown as T;
                    }

                    const blobUrl = await this.convertTextureToBlobUrl(texture, options);
                    if (blobUrl) {
                        this.textureToBlobCache.set(texture, {
                            blobUrl,
                            version: texture.version,
                            width: requestedWidth,
                            height: requestedHeight,
                        });
                        return blobUrl as unknown as T;
                    }
                    else {
                        console.warn("TextureConverter: Failed to convert texture to blob url.", texture.type);
                    }
                }
                else {
                    throw new BugIndicatingError(`TextureConverter: Cannot convert value to blob:url (value type is ${typeof value})`);
                }
            }

            // throw new BugIndicatingError(`TextureConverter: Cannot convert value to target type ${targetType} (value type is ${typeof value})`);

            return null;
        })
    }

    private async convertTextureToBlobUrl(texture: Texture, options: ValueConverterOptions): Promise<string | null> {

        if (isCubeTexture(texture)) {
            const dataUrl = this.renderCubeTextureToCanvas(texture, options);
            if (dataUrl) {
                return dataUrl;
            }
        }

        const renderer = this.three.findRendererForTexture(texture);
        if (renderer) {
            const tex = await this.renderTextureWithRenderer(texture, renderer, options);
            if (tex) {
                return tex;
            }
        }
        else {
            this.logger.warn("TextureConverter: No renderer found for texture " + texture.type + " " + texture.name);
        }


        const image = texture.image;
        const source = texture.source;


        if (isDataTexture(texture)) {
            const width = image.width;
            const height = image.height;
            const data = image.data;
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            if (ctx) {
                const imageData = ctx.createImageData(width, height);
                imageData.data.set(data);
                ctx.putImageData(imageData, 0, 0);
                return canvas.toDataURL()
            }
        }

        if (image instanceof HTMLImageElement || image instanceof HTMLCanvasElement || image instanceof ImageBitmap) {
            const canvas = document.createElement("canvas");
            canvas.width = image.width;
            canvas.height = image.height;
            const ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.drawImage(image, 0, 0);
                return canvas.toDataURL()
            }
        }

        if (source && source.data instanceof HTMLImageElement) {
            const img = source.data as HTMLImageElement;
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.drawImage(img, 0, 0);
                return canvas.toDataURL()
            }
        }

        return null;
    }



    // Limit the maximum size to avoid huge memory consumption. These values are used when no width/height is specified in the options.
    private readonly maxDefaultTextureSize = 1024;
    private readonly maxDefaultTextureWidth = 1024;

    private _canvas: HTMLCanvasElement | null = null;
    private async renderTextureWithRenderer(texture: Texture | CompressedTexture | CubeTexture | WebGLRenderTarget, renderer: WebGLRenderer, options: ValueConverterOptions): Promise<string | null> {

        // renderer = shallowCloneObject(renderer);
        // renderer = new WebGLRenderer();

        if (isWebGLRenderTarget(texture)) {
            texture = texture.texture;
        }
        if (isCubeTexture(texture)) {

        }

        let width = options.width || Math.min(this.maxDefaultTextureSize, texture.width || Number.MAX_SAFE_INTEGER);
        let height = options.height || Math.min(this.maxDefaultTextureWidth, texture.height || Number.MAX_SAFE_INTEGER);

        if (width > 4096) width = 4_096;
        if (height > 4096) height = 4_096;
        else if (width > texture.width) width = texture.width;
        else if (height > texture.height) height = texture.height;

        const originalRenderTarget = renderer.getRenderTarget();
        const originalChannel = texture.channel;
        const originalRepeat = texture.repeat.clone();
        const originalOffset = texture.offset.clone();
        const originalWrapS = texture.wrapS;
        const originalWrapT = texture.wrapT;
        const originalRotation = texture.rotation;
        const originalCenter = texture.center.clone();

        // @TODO: this easily breaks if we're using a different three.js version (more modern) e.g. with webgpu

        const renderTarget = new WebGLRenderTarget(width, height);
        const material = getMaterial();
        const geometry = new PlaneGeometry(2, 2);

        try {
            renderer.setRenderTarget(renderTarget);
            texture.channel = 0;
            texture.repeat.set(1, 1);
            texture.offset.set(0, 0);
            texture.wrapS = texture.wrapT = 1000; // ClampToEdgeWrapping
            texture.rotation = 0;
            texture.center.set(0.5, 0.5);

            const camera = new OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
            camera.name = "TextureConverter Render Camera";
            camera.position.z = 1;

            const scene = new Scene();
            scene.name = "TextureConverter Render Scene";
            HierarchyNode.setFlags(scene, NodeFlags.HideInHierarchy);
            const mesh = new Mesh(geometry, material);
            mesh.name = "TextureConverter Render Mesh";
            scene.add(mesh);

            this.logger.debug("Rendering texture " + texture.type + " " + texture.name + " to blob url with size " + width + "x" + height);
            renderer.render(scene, camera);
            return await this.readbackWebGLRenderTarget(renderTarget, renderer);
        }
        catch (error) {
            this.logger.error(`Failed to render texture ${texture.type} (${texture.name})`, error);
        }
        finally {
            texture.channel = originalChannel;
            texture.repeat.copy(originalRepeat);
            texture.offset.copy(originalOffset);
            texture.wrapS = originalWrapS;
            texture.wrapT = originalWrapT;
            texture.rotation = originalRotation;
            texture.center.copy(originalCenter);

            renderer.setRenderTarget(originalRenderTarget);
            renderTarget.dispose();
            geometry.dispose();
            material.dispose();
        }


        return null;

        function getMaterial() {

            return new MeshBasicMaterial({ map: texture });
            // let colorNode = texNode;
            // const texNode = texture(
            //     s,
            //     vec2(uvNode.x, sub(1, uvNode.y)),
            //     mipmapUniform,
            // );

        }
    }

    private async readbackWebGLRenderTarget(texture: WebGLRenderTarget, renderer: WebGLRenderer): Promise<string | null> {
        const width = texture.width;
        const height = texture.height;
        const pixels = new Uint8Array(width * height * 4);

        // WebGL Renderer has this method https://threejs.org/docs/#WebGLRenderer.readRenderTargetPixels
        if ("readRenderTargetPixels" in renderer) {
            renderer.readRenderTargetPixels(texture, 0, 0, width, height, pixels);
        }
        // in three 182 we have Renderer and only readRenderTargetPixelsAsync: https://threejs.org/docs/?q=readRenderTargetPixels#Renderer.readRenderTargetPixelsAsync
        else if ("readRenderTargetPixelsAsync" in renderer) {
            const arr: TypedArray = await (renderer as any).readRenderTargetPixelsAsync(texture, 0, 0, width, height);
            pixels.set(new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength));
        }

        // Create canvas
        const canvas = this._canvas ??= document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
            const imageData = ctx.createImageData(width, height);
            imageData.data.set(pixels);
            ctx.putImageData(imageData, 0, 0);
            return canvas.toDataURL();
        }
        return null;
    }

    private renderCubeTextureToCanvas(
        cubeTexture: CubeTexture,
        options: ValueConverterOptions
    ): string | null {

        let faceSize = options.width || 512;
        if (faceSize > 1024) faceSize = 1024;
        if (cubeTexture.image && cubeTexture.image[0]) {
            const img = cubeTexture.image[0];
            if (img.width && img.height) {
                faceSize = Math.min(img.width, img.height, faceSize);
            }
        }

        const canvas = this._canvas ??= document.createElement("canvas");
        canvas.width = faceSize * 3;
        canvas.height = faceSize * 2;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;

        const facePositions = [
            { x: 2, y: 1 }, // PX (right)
            { x: 0, y: 1 }, // NX (left)
            { x: 1, y: 0 }, // PY (top)
            { x: 1, y: 2 }, // NY (bottom)
            { x: 1, y: 1 }, // PZ (front)
            { x: 3, y: 1 }, // NZ (back)
        ];

        try {
            for (let i = 0; i < 6; i++) {
                const faceImage = cubeTexture.image[i];
                if (!faceImage) continue;

                const pos = facePositions[i];
                ctx.drawImage(
                    faceImage,
                    pos.x * faceSize,
                    pos.y * faceSize,
                    faceSize,
                    faceSize
                );
            }

            return canvas.toDataURL();
        } catch (e) {
            console.error('CubeTexture rendering error:', e);
            return null;
        }
    }

    // // Apply exposure using uniform
    // // @ts-ignore
    // colorNode = vec4(
    //     colorNode.xyz.mul(pow(2.0, exposureUniform)),
    //     colorNode.w,
    // );

    // // Apply RGBA filtering based on uniform
    // // @ts-ignore
    // colorNode = select(
    //     equal(filterModeUniform, int(1)), // R
    //     vec4(colorNode.x, colorNode.x, colorNode.x, 1.0),
    //     select(
    //         equal(filterModeUniform, int(2)), // G
    //         vec4(colorNode.y, colorNode.y, colorNode.y, 1.0),
    //         select(
    //             equal(filterModeUniform, int(3)), // B
    //             vec4(colorNode.z, colorNode.z, colorNode.z, 1.0),
    //             select(
    //                 equal(filterModeUniform, int(4)), // A
    //                 vec4(
    //                     colorNode.w,
    //                     colorNode.w,
    //                     colorNode.w,
    //                     1.0,
    //                 ),
    //                 vec4(
    //                     colorNode.x,
    //                     colorNode.y,
    //                     colorNode.z,
    //                     colorNode.w,
    //                 ), // RGBA
    //             ),
    //         ),
    //     ),
    // );

    // // @ts-ignore
    // mat.colorNode = colorNode.xyz;
    // mat.opacityNode = colorNode.w;
}

// ValueConversionService.registerConverter(textureConverter);
